(() => {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  let dialog;
  let image;
  let stage;
  let previousButton;
  let nextButton;
  let scale = 1;
  let x = 0;
  let y = 0;
  let startDistance = 0;
  let startScale = 1;
  let dragOrigin = null;
  let swipeOrigin = null;
  let galleryTap = null;
  let suppressClickUntil = 0;
  let galleryItems = [];
  let activeIndex = 0;
  const pointers = new Map();

  const labels = () => {
    const lang = document.documentElement.lang.toLowerCase();
    if (lang.startsWith('el')) return { close: 'Κλείσιμο', zoomIn: 'Μεγέθυνση', zoomOut: 'Σμίκρυνση', reset: 'Επαναφορά', previous: 'Προηγούμενη φωτογραφία', next: 'Επόμενη φωτογραφία' };
    return { close: 'Close', zoomIn: 'Zoom in', zoomOut: 'Zoom out', reset: 'Reset zoom', previous: 'Previous image', next: 'Next image' };
  };

  const render = () => {
    image.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
  };

  const reset = () => {
    scale = 1;
    x = 0;
    y = 0;
    render();
  };

  const setScale = (nextScale) => {
    scale = clamp(nextScale, 1, 5);
    if (scale === 1) {
      x = 0;
      y = 0;
    }
    render();
  };

  const largestSource = (element) => {
    if (!element) return '';
    const candidates = (element.srcset || '').split(',').map((candidate) => {
      const parts = candidate.trim().split(/\s+/);
      return { url: parts[0], width: Number.parseInt(parts[1], 10) || 0 };
    }).filter((candidate) => candidate.url);
    candidates.sort((a, b) => b.width - a.width);
    return candidates[0]?.url || element.currentSrc || element.src || element.dataset.src || '';
  };

  const itemFromTrigger = (trigger) => {
    const zoomLink = trigger.matches?.('.atlantic-product-zoom-trigger') ? trigger : trigger.closest?.('.atlantic-product-zoom-trigger');
    const visibleImage = trigger.matches?.('img') ? trigger : trigger.querySelector?.('img:not(.zoom-image)');
    const source = zoomLink?.dataset.atlanticProductZoom || zoomLink?.querySelector('.js-zoom-image')?.dataset.src || largestSource(visibleImage);
    return source ? { trigger, source, alt: visibleImage?.alt || zoomLink?.querySelector('.product-image')?.alt || '' } : null;
  };

  const collectGalleryItems = (trigger) => {
    const gallery = trigger.closest?.('media-gallery, .media-gallery') || document.querySelector('media-gallery, .media-gallery');
    const candidates = gallery ? gallery.querySelectorAll('.atlantic-product-zoom-trigger') : [trigger];
    const seen = new Set();
    return [...candidates].map(itemFromTrigger).filter((item) => {
      if (!item || seen.has(item.source)) return false;
      seen.add(item.source);
      return true;
    });
  };

  const updateNavigation = () => {
    const hasMultiple = galleryItems.length > 1;
    previousButton.hidden = !hasMultiple;
    nextButton.hidden = !hasMultiple;
  };

  const showItem = (index) => {
    if (!galleryItems.length) return;
    activeIndex = (index + galleryItems.length) % galleryItems.length;
    const item = galleryItems[activeIndex];
    image.src = item.source;
    image.alt = item.alt;
    reset();
    updateNavigation();
  };

  const move = (direction) => {
    if (galleryItems.length < 2) return;
    showItem(activeIndex + direction);
  };

  const build = () => {
    const text = labels();
    dialog = document.createElement('dialog');
    dialog.className = 'atlantic-product-zoom';
    dialog.setAttribute('aria-label', text.zoomIn);
    dialog.innerHTML = `<div class="atlantic-product-zoom__controls"><button class="atlantic-product-zoom__button" type="button" data-atlantic-zoom-out aria-label="${text.zoomOut}">−</button><button class="atlantic-product-zoom__button" type="button" data-atlantic-zoom-reset aria-label="${text.reset}">1×</button><button class="atlantic-product-zoom__button" type="button" data-atlantic-zoom-in aria-label="${text.zoomIn}">+</button><button class="atlantic-product-zoom__button" type="button" data-atlantic-zoom-close aria-label="${text.close}">×</button></div><button class="atlantic-product-zoom__nav atlantic-product-zoom__nav--previous" type="button" data-atlantic-zoom-previous aria-label="${text.previous}">‹</button><div class="atlantic-product-zoom__stage"><img class="atlantic-product-zoom__image" alt=""></div><button class="atlantic-product-zoom__nav atlantic-product-zoom__nav--next" type="button" data-atlantic-zoom-next aria-label="${text.next}">›</button>`;
    document.body.append(dialog);
    image = dialog.querySelector('.atlantic-product-zoom__image');
    stage = dialog.querySelector('.atlantic-product-zoom__stage');
    previousButton = dialog.querySelector('[data-atlantic-zoom-previous]');
    nextButton = dialog.querySelector('[data-atlantic-zoom-next]');

    dialog.querySelector('[data-atlantic-zoom-close]').addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-atlantic-zoom-in]').addEventListener('click', () => setScale(scale + 0.5));
    dialog.querySelector('[data-atlantic-zoom-out]').addEventListener('click', () => setScale(scale - 0.5));
    dialog.querySelector('[data-atlantic-zoom-reset]').addEventListener('click', reset);
    previousButton.addEventListener('click', () => move(-1));
    nextButton.addEventListener('click', () => move(1));
    dialog.addEventListener('close', reset);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') move(-1);
      if (event.key === 'ArrowRight') move(1);
    });
    stage.addEventListener('dblclick', () => setScale(scale === 1 ? 2.5 : 1));
    stage.addEventListener('wheel', (event) => {
      event.preventDefault();
      setScale(scale + (event.deltaY < 0 ? 0.25 : -0.25));
    }, { passive: false });
    stage.addEventListener('pointerdown', (event) => {
      stage.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 1) {
        dragOrigin = { pointerX: event.clientX, pointerY: event.clientY, x, y };
        swipeOrigin = { x: event.clientX, y: event.clientY };
      }
      if (pointers.size === 2) {
        const points = [...pointers.values()];
        startDistance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        startScale = scale;
        swipeOrigin = null;
      }
    });
    stage.addEventListener('pointermove', (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2) {
        const points = [...pointers.values()];
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        setScale(startScale * distance / startDistance);
      } else if (scale > 1 && dragOrigin) {
        x = dragOrigin.x + event.clientX - dragOrigin.pointerX;
        y = dragOrigin.y + event.clientY - dragOrigin.pointerY;
        render();
      }
    });
    const release = (event) => {
      const origin = swipeOrigin;
      pointers.delete(event.pointerId);
      dragOrigin = null;
      swipeOrigin = null;
      if (scale !== 1 || !origin || pointers.size) return;
      const deltaX = event.clientX - origin.x;
      const deltaY = event.clientY - origin.y;
      if (Math.abs(deltaX) > 55 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25) move(deltaX < 0 ? 1 : -1);
    };
    stage.addEventListener('pointerup', release);
    stage.addEventListener('pointercancel', release);
  };

  const openFromTrigger = (trigger) => {
    if (!dialog) build();
    galleryItems = collectGalleryItems(trigger);
    const clickedItem = itemFromTrigger(trigger);
    activeIndex = Math.max(0, galleryItems.findIndex((item) => item.source === clickedItem?.source));
    if (!galleryItems.length && clickedItem) galleryItems = [clickedItem];
    if (!galleryItems.length) return;
    showItem(activeIndex);
    if (!dialog.open) dialog.showModal();
  };

  const galleryTriggerFromEvent = (event) => {
    const pathTrigger = event.composedPath().find((element) => element.matches?.('.atlantic-product-zoom-trigger'));
    if (pathTrigger) return pathTrigger;
    const point = event.changedTouches?.[0] || event;
    return document.elementFromPoint(point.clientX, point.clientY)?.closest('.atlantic-product-zoom-trigger');
  };

  document.addEventListener('pointerdown', (event) => {
    const trigger = galleryTriggerFromEvent(event);
    galleryTap = trigger ? { trigger, x: event.clientX, y: event.clientY, pointerId: event.pointerId } : null;
  }, true);

  document.addEventListener('pointerup', (event) => {
    if (!galleryTap || galleryTap.pointerId !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - galleryTap.x, event.clientY - galleryTap.y);
    const trigger = galleryTap.trigger;
    galleryTap = null;
    if (distance > 10) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    suppressClickUntil = Date.now() + 700;
    openFromTrigger(trigger);
  }, true);

  document.addEventListener('pointercancel', () => {
    galleryTap = null;
  }, true);

  document.addEventListener('click', (event) => {
    const trigger = galleryTriggerFromEvent(event);
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (Date.now() < suppressClickUntil) return;
    openFromTrigger(trigger);
  }, true);
})();
